/**
 * usePhoneOperators — fetches the admin-maintained UAE phone-operator list
 * (name_ar/name_en, codes, logo_asset_id) once per session and caches it in
 * a module-level map. Any component that needs the dropdown/filter list
 * calls this hook; the real network call fires only on the first mount.
 *
 * Shape returned by backend proxy (/api/platform-lists/phone-operators):
 *   { success, version,
 *     data: [{id, name_ar, name_en, codes[], logo_asset_id, updated_at}] }
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Minimal fallback shown only if the admin API is completely unreachable.
// No logo_asset_id — forces the component to use a bundled image.
const FALLBACK = [
  { id: 'etisalat', name_ar: 'اتصالات', name_en: 'Etisalat', codes: ['050', '054', '056'], logo_asset_id: null },
  { id: 'du',       name_ar: 'دو',      name_en: 'Du',       codes: ['052', '055'],        logo_asset_id: null },
];

// Module-level cache so navigation between pages doesn't retrigger the fetch.
let _cache = null;
let _inflight = null;

export default function usePhoneOperators() {
  const [operators, setOperators] = useState(() => _cache || FALLBACK);
  const [loading, setLoading] = useState(() => !_cache);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (_cache) return;

    const run = async () => {
      try {
        if (!_inflight) {
          _inflight = axios
            .get(`${BACKEND_URL}/api/platform-lists/phone-operators`, { timeout: 10000 })
            .then((resp) => {
              const data = (resp.data && resp.data.data) || [];
              const normalised = data.map((op) => ({
                id: op.id || op.name_en || '',
                name_ar: op.name_ar || op.name_en || '',
                name_en: op.name_en || op.name_ar || '',
                codes: Array.isArray(op.codes) ? op.codes : [],
                logo_asset_id: op.logo_asset_id || null,
              }));
              _cache = normalised.length ? normalised : FALLBACK;
              return _cache;
            })
            .catch(() => {
              _cache = FALLBACK;
              return _cache;
            })
            .finally(() => {
              setTimeout(() => { _inflight = null; }, 0);
            });
        }
        const list = await _inflight;
        if (mounted.current) {
          setOperators(list);
          setLoading(false);
        }
      } catch (err) {
        if (mounted.current) {
          setError(err);
          setLoading(false);
        }
      }
    };
    run();
  }, []);

  return { operators, loading, error };
}

/**
 * assetUrl — build the URL that streams the admin-side logo binary for a
 * given `logo_asset_id`. Returns `null` when the id is missing so callers
 * can display their bundled fallback image.
 */
export function assetUrl(assetId) {
  if (!assetId) return null;
  return `${BACKEND_URL}/api/platform-lists/asset/${encodeURIComponent(assetId)}`;
}
